/**
 * 热启动优化器
 * 专门用于提升 dev 和 build 命令的启动速度
 */

import chalk from 'chalk';
import ora from 'ora';
import {
  SynthiaCacheManager,
  CacheVersionManager,
  CacheStrategyManager,
  CacheMetricsCollector,
  type CacheConfig,
} from './index';
import path from 'path';
import fs from 'fs-extra';

/**
 * 热启动优化器
 */
export class HotStartOptimizer {
  private cacheManager: SynthiaCacheManager | null = null;
  private versionManager: CacheVersionManager | null = null;
  private strategyManager: CacheStrategyManager | null = null;
  private metricsCollector: CacheMetricsCollector | null = null;
  private spinner = ora();

  constructor() {}

  /**
   * 初始化热启动优化器
   */
  async initialize(config?: Partial<CacheConfig>): Promise<void> {
    this.spinner.start('初始化热启动优化器...');

    try {
      const defaultConfig: CacheConfig = {
        local: {
          dir: '.synthia-cache',
          maxSize: 200 * 1024 * 1024, // 200MB
          ttl: 7 * 24 * 60 * 60 * 1000, // 7天
          compression: true,
        },
        cloud: {
          enabled: false,
          provider: 'aws-s3',
          bucket: '',
          region: 'us-east-1',
          accessKeyId: '',
          accessKeySecret: '',
        },
        strategy: {
          level: 'local',
          localFirst: true,
          autoSync: false,
          syncInterval: 5 * 60 * 1000,
        },
        ...config,
      };

      this.cacheManager = new SynthiaCacheManager(defaultConfig);
      this.versionManager = new CacheVersionManager();
      this.strategyManager = new CacheStrategyManager(this.cacheManager);
      this.metricsCollector = new CacheMetricsCollector();

      this.spinner.succeed('热启动优化器初始化完成');
    } catch (error) {
      this.spinner.fail('热启动优化器初始化失败');
      console.error(chalk.red('错误:'), error);
      throw error;
    }
  }

  /**
   * 优化开发模式启动
   */
  async optimizeDevStart(context: {
    projectName: string;
    projectVersion: string;
    devServerConfig: any;
    sourceFiles: string[];
  }): Promise<{
    useCache: boolean;
    cachedConfig?: any;
    cachedDependencies?: any;
    startupTime: number;
  }> {
    const startTime = Date.now();
    this.spinner.start('优化开发模式启动...');

    try {
      if (!this.cacheManager || !this.versionManager) {
        await this.initialize();
      }

      const projectKey = `dev:${context.projectName}:${context.projectVersion}`;

      // 检查项目配置缓存
      const configKey = `${projectKey}:config`;
      const cachedConfig = await this.cacheManager!.get(configKey);

      // 检查依赖缓存
      const depsKey = `${projectKey}:dependencies`;
      const cachedDependencies = await this.cacheManager!.get(depsKey);

      // 检查源文件是否变化
      const sourceFilesKey = `${projectKey}:source-files`;
      const cachedSourceFiles = await this.cacheManager!.get(sourceFilesKey);

      let useCache = false;

      if (cachedConfig && cachedDependencies && cachedSourceFiles) {
        // 检查源文件是否变化
        const currentSourceFiles = context.sourceFiles.sort();
        const cachedFiles = cachedSourceFiles.sort();

        if (
          JSON.stringify(currentSourceFiles) === JSON.stringify(cachedFiles)
        ) {
          useCache = true;
          console.log(chalk.green('✅ 使用缓存配置，跳过配置解析'));
        } else {
          console.log(chalk.yellow('⚠️  源文件已变化，重新解析配置'));
        }
      } else {
        console.log(chalk.blue('ℹ️  首次启动，创建配置缓存'));
      }

      // 缓存当前配置
      await this.cacheManager!.set(configKey, context.devServerConfig, {
        tags: ['dev', 'config'],
        expiresAt: Date.now() + 24 * 60 * 60 * 1000, // 24小时
      });

      // 缓存依赖信息
      const dependencies = await this.getProjectDependencies();
      await this.cacheManager!.set(depsKey, dependencies, {
        tags: ['dev', 'dependencies'],
        expiresAt: Date.now() + 12 * 60 * 60 * 1000, // 12小时
      });

      // 缓存源文件列表
      await this.cacheManager!.set(sourceFilesKey, context.sourceFiles, {
        tags: ['dev', 'source-files'],
        expiresAt: Date.now() + 60 * 60 * 1000, // 1小时
      });

      const startupTime = Date.now() - startTime;
      this.spinner.succeed(`开发模式启动优化完成 (${startupTime}ms)`);

      return {
        useCache,
        cachedConfig: useCache ? cachedConfig : undefined,
        cachedDependencies: useCache ? cachedDependencies : undefined,
        startupTime,
      };
    } catch (error) {
      this.spinner.fail('开发模式启动优化失败');
      console.error(chalk.red('错误:'), error);
      throw error;
    }
  }

  /**
   * 优化构建模式启动
   */
  async optimizeBuildStart(context: {
    projectName: string;
    projectVersion: string;
    buildConfig: any;
    sourceFiles: string[];
  }): Promise<{
    useCache: boolean;
    cachedBuild?: any;
    incrementalBuild: boolean;
    startupTime: number;
  }> {
    const startTime = Date.now();
    this.spinner.start('优化构建模式启动...');

    try {
      if (!this.cacheManager || !this.versionManager) {
        await this.initialize();
      }

      const buildKey = `build:${context.projectName}:${context.projectVersion}`;

      // 检查构建缓存
      const cachedBuild = await this.cacheManager!.get(buildKey);

      let useCache = false;
      let incrementalBuild = false;

      if (cachedBuild) {
        // 检查依赖是否变化
        const dependencies = [
          'package.json',
          'tsconfig.json',
          ...context.sourceFiles,
        ];

        const isValid = this.versionManager!.isCacheValid(
          buildKey,
          dependencies
        );

        if (isValid) {
          useCache = true;
          console.log(chalk.green('✅ 构建缓存有效，使用缓存结果'));
        } else {
          incrementalBuild = true;
          console.log(chalk.yellow('⚠️  构建缓存已过期，进行增量构建'));
        }
      } else {
        console.log(chalk.blue('ℹ️  首次构建，创建构建缓存'));
      }

      // 预热构建相关缓存
      const buildCacheKeys = [
        'build:dependencies',
        'build:config',
        'build:assets',
        'build:modules',
      ];

      console.log(chalk.yellow('🔥 预热构建缓存...'));
      await this.strategyManager!.warmup(buildCacheKeys);

      const startupTime = Date.now() - startTime;
      this.spinner.succeed(`构建模式启动优化完成 (${startupTime}ms)`);

      return {
        useCache,
        cachedBuild: useCache ? cachedBuild : undefined,
        incrementalBuild,
        startupTime,
      };
    } catch (error) {
      this.spinner.fail('构建模式启动优化失败');
      console.error(chalk.red('错误:'), error);
      throw error;
    }
  }

  /**
   * 保存构建结果
   */
  async saveBuildResult(context: {
    projectName: string;
    projectVersion: string;
    buildResult: any;
    sourceFiles: string[];
  }): Promise<void> {
    try {
      const buildKey = `build:${context.projectName}:${context.projectVersion}`;

      const buildData = {
        timestamp: Date.now(),
        version: context.projectVersion,
        result: context.buildResult,
        sourceFiles: context.sourceFiles,
      };

      const dependencies = [
        'package.json',
        'tsconfig.json',
        ...context.sourceFiles,
      ];

      const meta = this.versionManager!.updateCacheVersion(
        buildKey,
        buildData,
        dependencies,
        ['build', context.projectName],
        context.projectVersion
      );

      await this.cacheManager!.set(buildKey, buildData, meta);
      console.log(chalk.green('✅ 构建结果已缓存'));
    } catch (error) {
      console.error(chalk.red('保存构建结果失败:'), error);
    }
  }

  /**
   * 获取项目依赖信息
   */
  private async getProjectDependencies(): Promise<any> {
    try {
      const packageJsonPath = path.join(process.cwd(), 'package.json');
      if (fs.existsSync(packageJsonPath)) {
        const packageJson = JSON.parse(
          fs.readFileSync(packageJsonPath, 'utf-8')
        );
        return {
          dependencies: packageJson.dependencies || {},
          devDependencies: packageJson.devDependencies || {},
          peerDependencies: packageJson.peerDependencies || {},
          timestamp: Date.now(),
        };
      }

      return {};
    } catch (error) {
      console.warn(chalk.yellow('获取项目依赖失败:'), error);
      return {};
    }
  }

  /**
   * 生成启动性能报告
   */
  async generateStartupReport(): Promise<void> {
    if (this.metricsCollector) {
      console.log(chalk.blue('📊 生成启动性能报告...'));
      await this.metricsCollector.generateReport();
    }
  }

  /**
   * 清理优化器
   */
  async cleanup(): Promise<void> {
    this.cacheManager = null;
    this.versionManager = null;
    this.strategyManager = null;
    this.metricsCollector = null;
  }
}

/**
 * 创建热启动优化器实例
 */
export function createHotStartOptimizer(): HotStartOptimizer {
  return new HotStartOptimizer();
}

/**
 * 默认热启动优化器实例
 */
export const hotStartOptimizer = createHotStartOptimizer();
